import React, { useEffect } from "react";
import {
  Row,
  Col,
  Label,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormGroup,
} from "reactstrap";
import { Calendar } from "react-feather";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import Flatpickr from "react-flatpickr";
import {
  fetchCustomersList,
  fetchSuppliersList,
  fetchVehiclesList,
} from "../../../slices/thunks";
import { fetchTaxList } from "../../../slices/TaxMaster/thunk";
import { createSelector } from "reselect";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { fetchDailyGSTReport } from "../../../slices/GSTReport/thunk";

const validationSchema = Yup.object({
  customerId: Yup.string().required("Customer is required"),
});

const Gst = ({ onCloseForm }) => {
  const dispatch = useDispatch();

  const selector = createSelector(
    (state) => state,
    (state) => ({
      taxes: state.TaxMaster.taxes,
      suppliers: state.Supplier.suppliers,
      customersList: state.Customer.customers,
      vehicles: state.Vehicle.vehicles,
      activeSessions: state.Session.selectedSessionYear,
    })
  );

  const { suppliers, customersList, activeSessions, vehicles } =
    useSelector(selector);

  useEffect(() => {
    dispatch(fetchTaxList({ isPagination: 0 }));
    dispatch(fetchVehiclesList({ isPagination: 0 }));
    dispatch(fetchCustomersList({ isPagination: 0 }));
    dispatch(fetchSuppliersList({ isPagination: 0 }));
  }, [dispatch]);

  return (
    <div
      className="page-content d-flex justify-content-center align-items-center"
      style={{ minHeight: "100vh", background: "#f8f9fa" }}
    >
      <div className="w-100" style={{ maxWidth: "900px" }}>
        <Card className="shadow-sm">
          <CardHeader className="bg-white">
            <h4 className="mb-0 text-start">Sales Invoice Report (GST)</h4>
          </CardHeader>

          <CardBody>
            <Formik
              enableReinitialize
              initialValues={{
                customerId: "",
                sessionId: activeSessions?.id || "",
                vehicleId: "",
                supplierId: "",
                fromDate: null,
                toDate: null,
              }}
              validationSchema={validationSchema}
              onSubmit={(values, { resetForm }) => {
                dispatch(fetchDailyGSTReport(values));
                resetForm();
                onCloseForm && onCloseForm();
              }}
            >
              {({ values, setFieldValue }) => (
                <Form>
                  <Row className="g-4">
                    {/* Left Column */}
                    <Col md={6}>
                      {/* SESSION */}
                      <FormGroup>
                        <Label className="form-label">Session</Label>
                        <input
                          type="text"
                          className="form-control bg-light"
                          disabled
                          value={activeSessions?.name || ""}
                        />
                      </FormGroup>

                      {/* CUSTOMER */}
                      <FormGroup>
                        <Label className="form-label">
                          Customer <span className="text-danger">*</span>
                        </Label>
                        <Select
                          options={customersList.map((c) => ({
                            value: c.id,
                            label: c.name,
                          }))}
                          value={
                            values.customerId
                              ? {
                                  value: values.customerId,
                                  label:
                                    customersList.find(
                                      (c) => c.id === values.customerId
                                    )?.name || "",
                                }
                              : null
                          }
                          onChange={(opt) => {
                            setFieldValue("customerId", opt?.value || "");
                          }}
                          placeholder="Select Customer"
                          classNamePrefix="react-select"
                        />
                        <ErrorMessage
                          name="customerId"
                          component="div"
                          className="text-danger small mt-1"
                        />
                      </FormGroup>

                      {/* FROM DATE */}
                      <FormGroup style={{ position: "relative" }}>
                        <Label className="form-label">From Date</Label>
                        <div style={{ position: "relative" }}>
                          <Flatpickr
                            className="form-control pe-5"
                            value={values.fromDate}
                            onChange={(date) =>
                              setFieldValue(
                                "fromDate",
                                date?.[0]?.toISOString().substring(0, 10) ||
                                  null
                              )
                            }
                          />
                          <Calendar
                            size={18}
                            className="text-muted"
                            style={{
                              position: "absolute",
                              right: "12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              pointerEvents: "none",
                            }}
                          />
                        </div>
                      </FormGroup>
                    </Col>

                    {/* Right Column */}
                    <Col md={6}>
                      {/* SUPPLIER */}
                      <FormGroup>
                        <Label className="form-label">Supplier</Label>
                        <Select
                          options={suppliers?.map((s) => ({
                            value: s.id,
                            label: s.name,
                          }))}
                          value={
                            values.supplierId
                              ? {
                                  value: values.supplierId,
                                  label:
                                    suppliers.find(
                                      (s) => s.id === values.supplierId
                                    )?.name || "",
                                }
                              : null
                          }
                          onChange={(opt) => {
                            setFieldValue("supplierId", opt?.value || "");
                          }}
                          placeholder="Select Supplier"
                          isClearable
                          classNamePrefix="react-select"
                        />
                      </FormGroup>

                      {/* VEHICLE */}
                      <FormGroup>
                        <Label className="form-label">Vehicle</Label>
                        <Select
                          options={vehicles?.map((v) => ({
                            value: v.id,
                            label: v.vehicleNo,
                          }))}
                          value={
                            values.vehicleId
                              ? {
                                  value: values.vehicleId,
                                  label:
                                    vehicles.find(
                                      (v) => v.id === values.vehicleId
                                    )?.vehicleNo || "",
                                }
                              : null
                          }
                          onChange={(opt) => {
                            setFieldValue("vehicleId", opt?.value || "");
                          }}
                          placeholder="Select Vehicle"
                          isClearable
                          classNamePrefix="react-select"
                        />
                      </FormGroup>

                      {/* TO DATE */}
                      <FormGroup style={{ position: "relative" }}>
                        <Label className="form-label">To Date</Label>
                        <div style={{ position: "relative" }}>
                          <Flatpickr
                            className="form-control pe-5"
                            value={values.toDate}
                            onChange={(date) =>
                              setFieldValue(
                                "toDate",
                                date?.[0]?.toISOString().substring(0, 10) ||
                                  null
                              )
                            }
                          />
                          <Calendar
                            size={18}
                            className="text-muted"
                            style={{
                              position: "absolute",
                              right: "12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              pointerEvents: "none",
                            }}
                          />
                        </div>
                      </FormGroup>
                    </Col>

                    {/* SUBMIT BUTTON */}
                    <Col xs={12} className="text-center mt-2">
                      <Button
                        color="success"
                        className="px-5"
                        type="submit" // ✅ REQUIRED
                      >
                        Download Report
                      </Button>
                    </Col>
                  </Row>
                </Form>
              )}
            </Formik>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default Gst;
